/*
  Controller for sections/pb-spine-explainer.liquid. Tiny, and has no three.js
  in it: the scene bundle (assets/pb-spine-scene.js, ~145 KB gzipped) is only
  imported once the section comes within ~1 screen of the viewport, and never on
  Save-Data, without WebGL, or when the merchant turns 3D off. In every one of
  those cases the poster and the step copy are the whole experience, so nothing
  is lost.

  Scroll mapping: each step block names a scene state. The anchor line (middle
  of the free viewport) is placed between step centres to get a fractional
  index; the scene blends between the two neighbouring states, holding each one
  while its step sits near the anchor (smoothstep 0.25 to 0.75).

  Loaded as a module, so it runs once per page however many instances exist.
*/

const instances = new Map();
const FADE = 150; // px of the 3D a card may cover before it has fully dissolved
const smooth = (a, b, x) => { const t = Math.min(1, Math.max(0, (x - a) / (b - a))); return t * t * (3 - 2 * t); };

function hasWebGL() {
  try {
    const c = document.createElement('canvas');
    return !!(window.WebGLRenderingContext && (c.getContext('webgl2') || c.getContext('webgl')));
  } catch (e) { return false; }
}

function init(root) {
  if (instances.has(root)) return;
  const steps = Array.from(root.querySelectorAll('[data-pbsx-step]'));
  const stage = root.querySelector('[data-pbsx-stage]');
  const host = root.querySelector('[data-pbsx-canvas]');
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const desktop = window.matchMedia('(min-width: 990px)');
  const st = { scene: null, visible: false, ticket: 0, observers: [], index: -1 };

  function update() {
    st.ticket = 0;
    if (!steps.length) return;
    const vh = window.innerHeight;
    const stageBottom = desktop.matches ? 0 : stage.getBoundingClientRect().bottom;
    const anchor = desktop.matches ? vh * 0.5 : (Math.max(0, stageBottom) + vh) / 2;
    const centres = steps.map((el) => { const r = el.getBoundingClientRect(); return r.top + r.height / 2; });

    let f = 0;
    if (anchor <= centres[0]) f = 0;
    else if (anchor >= centres[centres.length - 1]) f = steps.length - 1;
    else {
      for (let i = 0; i < centres.length - 1; i++) {
        if (anchor >= centres[i] && anchor < centres[i + 1]) { f = i + (anchor - centres[i]) / (centres[i + 1] - centres[i]); break; }
      }
    }

    const i = Math.floor(f);
    const j = Math.min(steps.length - 1, i + 1);
    const t = smooth(0.25, 0.75, f - i);
    const active = t < 0.5 ? i : j;
    if (active !== st.index) {
      st.index = active;
      steps.forEach((el, k) => el.classList.toggle('is-active', k === active));
    }
    // Mobile: cards scroll OVER the pinned view. Each card's top dissolves
    // through a soft band that starts FADE px above the stage's bottom edge,
    // a moving wipe rather than a hard line.
    steps.forEach((el) => {
      const card = el.firstElementChild;
      if (!card) return;
      if (desktop.matches) { card.style.removeProperty('--pbsx-cut'); return; }
      const cut = stageBottom - FADE - card.getBoundingClientRect().top;
      card.style.setProperty('--pbsx-cut', cut.toFixed(1) + 'px');
    });

    root.dataset.pbsxProgress = f.toFixed(3);
    if (st.scene) {
      st.scene.setFocus(desktop.matches ? (+root.dataset.focus || 68) / 100 : 0.5);
      st.scene.setBlend(steps[i].dataset.scene, steps[j].dataset.scene, t);
    }
  }

  const request = () => { if (!st.ticket) st.ticket = requestAnimationFrame(update); };

  function play() {
    if (st.scene && st.visible && document.visibilityState === 'visible') st.scene.start();
    else if (st.scene) st.scene.stop();
  }

  async function load() {
    try {
      const mod = await import(root.dataset.sceneSrc);
      if (!instances.has(root)) return; // unloaded while fetching (theme editor)
      st.scene = mod.createSpineScene(host, { reducedMotion: reduced, maxDpr: desktop.matches ? 1.75 : 1.5, background: root.dataset.bg });
      update();
      st.scene.renderOnce();
      root.classList.add('is-3d');
      play();
    } catch (e) {
      root.classList.add('is-3d-failed');
      if (window.console) console.warn('[pb-spine-explainer] 3D unavailable, showing poster', e);
    }
  }

  const want3d = root.dataset.webgl === 'true'
    && !(navigator.connection && navigator.connection.saveData)
    && 'IntersectionObserver' in window
    && hasWebGL();

  if (want3d) {
    const near = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) { near.disconnect(); load(); }
    }, { rootMargin: '100% 0px' });
    near.observe(root);
    const onscreen = new IntersectionObserver((entries) => {
      st.visible = entries[entries.length - 1].isIntersecting;
      play();
    });
    onscreen.observe(stage);
    st.observers.push(near, onscreen);
  }

  const onVis = () => play();
  window.addEventListener('scroll', request, { passive: true });
  window.addEventListener('resize', request);
  document.addEventListener('visibilitychange', onVis);

  instances.set(root, {
    update,
    get scene() { return st.scene; },
    destroy() {
      window.removeEventListener('scroll', request);
      window.removeEventListener('resize', request);
      document.removeEventListener('visibilitychange', onVis);
      st.observers.forEach((o) => o.disconnect());
      if (st.ticket) cancelAnimationFrame(st.ticket);
      if (st.scene) st.scene.dispose();
      instances.delete(root);
    },
  });
  root.__pbsx = instances.get(root); // test hook: the Browser pane drops scroll events
  update();
}

function initAll(scope) {
  (scope || document).querySelectorAll('[data-pb-spine]').forEach(init);
}

initAll();

document.addEventListener('shopify:section:load', (e) => initAll(e.target));
document.addEventListener('shopify:section:unload', (e) => {
  e.target.querySelectorAll('[data-pb-spine]').forEach((r) => instances.get(r)?.destroy());
});
document.addEventListener('shopify:block:select', (e) => {
  const step = e.target.closest && e.target.closest('[data-pbsx-step]');
  if (step) step.scrollIntoView({ block: 'center', behavior: 'smooth' });
});
